/** The user record's states (plan "Page anatomy" → States): skeleton panels while the user
 *  loads (never a spinner over content), the records core's empty state for a user that
 *  does not exist or that the operator may not see, and the deleted-user banner — what it
 *  means, and Restore for whoever may manage users. */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { RecordEmptyState, RecordSkeleton } from '@/features/records/page/RecordStates';

const BACK = (
  <Button component={RouterLink} to="/users" startIcon={<ArrowBackIcon />} size="small">
    Back to users
  </Button>
);

export function UserNotFound({ id }: { id: number | string }) {
  return (
    <RecordEmptyState icon={<SearchOffIcon />} title={`User #${id} not found`} action={BACK}>
      No user has this id — the link may carry another record’s id. Search the users grids by name, email or phone
      instead; a soft-deleted user is listed there with its filter.
    </RecordEmptyState>
  );
}

export function UserNoPermission({ id }: { id: number | string }) {
  return (
    <RecordEmptyState icon={<LockOutlinedIcon />} title={`You can’t open user #${id}`} action={BACK}>
      Your console role does not include users. Ask an Ops admin for the console_user_view permission.
    </RecordEmptyState>
  );
}

/** The header's place and the panels while the user loads. */
export function UserLoading({ id }: { id: number }) {
  return (
    <Box>
      <PageHeader
        helpStory="users-detail-page"
        leading={<Skeleton variant="circular" width={32} height={32} />}
        title={`User #${id}`}
        subtitle={<Skeleton variant="text" width={420} />}
      />
      {/* The account band's five cells, then the panels. */}
      <RecordSkeleton label="Loading the user" nodes={5} />
    </Box>
  );
}

/** A soft-deleted user: it cannot sign in, the bookings list leaves its bookings out, and
 *  Restore (console_user_manage) brings it back — after a confirmation. An operator who
 *  could edit the user reads why Edit is disabled. */
export function DeletedUserBanner({
  userId,
  canRestore,
  onRestored,
}: {
  userId: number;
  canRestore: boolean;
  onRestored: () => void;
}) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const restore = useMutation({
    mutationFn: () => usersApi.restore(userId),
    onSuccess: () => {
      snackbar.show('User restored.', 'success');
      void qc.invalidateQueries({ queryKey: ['consoleUsers'] });
      onRestored();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Restore failed.', 'error'),
  });
  return (
    <>
      <Alert
        severity="info"
        icon={<DeleteOutlineIcon />}
        sx={{ mb: 2 }}
        data-deleted-banner
        action={
          canRestore ? (
            <Button color="inherit" size="small" onClick={() => setConfirm(true)} disabled={restore.isPending}>
              {restore.isPending ? 'Restoring…' : 'Restore'}
            </Button>
          ) : undefined
        }
      >
        <strong>Deleted</strong> — this account is soft-deleted: it cannot sign in, and the bookings list leaves its
        bookings out.
        {canRestore ? (
          <Box component="span" sx={{ display: 'block', mt: 0.25 }} data-edit-disabled>
            Edit disabled — a deleted user is read-only until it is restored.
          </Box>
        ) : (
          ' Restoring it needs the console_user_manage permission.'
        )}
      </Alert>
      {confirm && (
        <ConfirmDialog
          open
          title="Restore user?"
          message="Clears the deleted flag and reactivates the account."
          confirmLabel="Restore"
          onConfirm={() => {
            setConfirm(false);
            restore.mutate();
          }}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
}
